import {
  AccountGroup,
  AccountInfo,
  BaseTag,
  Card,
  Col,
  FieldLabel,
  Flex,
  Row,
  Space,
  Text,
  theme,
} from '@/components';
import { TagTarget } from '@/features/tag/components';
import { useParseUsersByIds } from '@/features/tenant-manager/user/_hooks/user';
import { ITEM_SPLITTER, dateFormat } from '@/utils';
import { PaperClipOutlined } from '@ant-design/icons';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { CommentPublishTag } from '@/features/comment/components/common/CommentPublishTag';
import type { CommentListElementModel } from '@/features/comment/models/comment';
import { CommentResolvedBadge } from '../../common/CommentResolvedBadge';
import { CommentUnreadBadge } from '../../common/CommentUnreadBadge';
import { PinpointDisplay } from '../../pinpoint';

type Props = {
  comment: z.infer<typeof CommentListElementModel>;
  onClick: () => void;
};

export const CommentCard: FC<Props> = ({ comment, onClick }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const userIdsForParse = [comment.authorId, ...comment.assigneeIds];
  const { parseUserById, parseUsersByIds } =
    useParseUsersByIds(userIdsForParse);
  const author = parseUserById(comment.authorId);
  const assignees = parseUsersByIds(comment.assigneeIds);

  return (
    <Card size="small" bordered={false} hoverable onClick={onClick}>
      <Flex vertical gap={8}>
        <Space
          align="center"
          style={{ justifyContent: 'space-between', width: '100%' }}
        >
          <CommentResolvedBadge resolved={comment.resolved} />
          {comment.readStatus === 'unread' && <CommentUnreadBadge />}
        </Space>
        {!!comment.tagTargets.length && (
          <Space wrap size={[0, 8]}>
            {comment.tagTargets.map((tagTarget) => (
              <TagTarget key={tagTarget.tagId} tag={tagTarget.tag} />
            ))}
          </Space>
        )}
        <Row align="top">
          <Col span={16}>
            {author && (
              <AccountInfo
                iconUrl={author.iconDataUrl}
                accountName={author.name}
                organizationName={author.organizations
                  ?.map((d) => d.name)
                  .join(ITEM_SPLITTER)}
              />
            )}
          </Col>
          <Col span={8}>
            <Text
              text={dateFormat({
                datetime: comment.createdAt,
                format: 'YYYY/MM/DD HH:mm',
              })}
              weight="regular"
              variant="footnote"
              color="secondary"
            />
          </Col>
        </Row>
        {comment.pinpoint && (
          <PinpointDisplay
            pinpoint={comment.pinpoint}
            showIcon
            color="secondary"
          />
        )}
        <Text maxRows={3} text={comment.content} weight="regular" />
        <Space>
          <CommentPublishTag publishType={comment.publishType} />
          {comment.label?.name && (
            <BaseTag color="white">{comment.label.name}</BaseTag>
          )}
          {!!comment.attachments?.length && (
            <PaperClipOutlined style={{ color: token.colorPrimary }} />
          )}
        </Space>
        {comment.publishType !== 'private' && (
          <Flex justify="flex-end" gap={4}>
            <FieldLabel size="small" text={t('comment.pinpoint-assignee')} />
            <AccountGroup
              accountInfoList={assignees.map((assignee) => ({
                iconUrl: assignee.iconDataUrl,
                accountName: assignee.name,
                organizationName: assignee.organizations
                  ?.map((d) => d.name)
                  .join(ITEM_SPLITTER),
              }))}
            />
          </Flex>
        )}
      </Flex>
    </Card>
  );
};
